import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Users, 
  Target, 
  Heart,
  ArrowRight,
  Star,
  Shield,
  Zap
} from 'lucide-react';
import { companyConfig, getCompanyTagline, getCompanyExperience, getCompanyExperienceDescription } from '../config/company';
import SEO from '../components/SEO';

const Company: React.FC = () => {
  const values = [
    {
      icon: <Shield className="h-8 w-8 text-primary-600" />,
      title: "Calidad",
      description: "Comprometidos con la excelencia en cada producto que fabricamos, utilizando materiales de primera calidad y procesos rigurosos de control."
    },
    {
      icon: <Heart className="h-8 w-8 text-primary-600" />,
      title: "Confianza",
      description: "Construimos relaciones duraderas con nuestros clientes basadas en la transparencia, honestidad y cumplimiento de compromisos."
    },
    {
      icon: <Zap className="h-8 w-8 text-primary-600" />,
      title: "Innovación",
      description: "Buscamos constantemente mejorar nuestros procesos y ofrecer soluciones innovadoras que superen las expectativas de nuestros clientes."
    },
    {
      icon: <Users className="h-8 w-8 text-primary-600" />,
      title: "Servicio",
      description: "Brindamos atención personalizada y asesoramiento técnico especializado para cada proyecto y necesidad específica."
    }
  ];

  const achievements = [
    {
      number: `${getCompanyExperience()}`,
      label: "Años de Experiencia",
      description: getCompanyExperienceDescription()
    },
    {
      number: "1000+",
      label: "Clientes Satisfechos",
      description: "Empresas que confían en nuestros productos y servicios"
    },
    {
      number: "50K+",
      label: "Bolsas Fabricadas",
      description: "Miles de bolsas entregadas con la más alta calidad"
    },
    {
      number: "100%",
      label: "Satisfacción",
      description: "Compromiso con la Satisfacción total del cliente"
    }
  ];

  const team = [
    {
      name: "Equipo técnico",
      role: "Especialistas en Producción",
      description: "Profesionales altamente capacitados en procesos de fabricación de bolsas de plástico LDPE."
    },
    {
      name: "Equipo Comercial",
      role: "Asesores de Ventas",
      description: "Expertos en brindar soluciones personalizadas y asesoramiento técnico especializado."
    },
    {
      name: "Equipo de Calidad",
      role: "Control de Calidad",
      description: "Responsables de garantizar que cada producto cumpla con los más altos estándares de calidad."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Empresa"
        description="Conoce la historia de Plsticos Polcen, fabricantes de bolsas LDPE en San Juan. Calidad, experiencia y atencin personalizada para comercios e industrias."
        keywords="empresa de bolsas plsticas, fbrica de bolsas LDPE, plsticos polcen, San Juan, fabricacin de bolsas"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nuestra Empresa
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              {getCompanyTagline()}
            </p>
            <p className="text-lg mb-10 text-primary-200 max-w-3xl mx-auto">
              En plásticos Polcen, nos especializamos en la fabricación de bolsas de plástico LDPE 
              personalizadas, ofreciendo calidad, confiabilidad y servicio personalizado a nuestros clientes.
            </p>
          </div>
        </div>
      </section>

      {/* Historia */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Nuestra Historia
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  {companyConfig.name} nació desde el año {companyConfig.foundationYear} con la visión de convertirse en una empresa 
                  líder en la fabricación de bolsas de plástico LDPE en la región de San Juan.
                </p>
                <p>
                  Desde nuestros inicios, nos hemos enfocado en ofrecer productos de alta calidad, 
                  personalizados según las necesidades específicas de cada cliente, manteniendo siempre 
                  los más altos estándares de calidad y servicio.
                </p>
                <p>
                  A lo largo de estos Años, hemos crecido y evolucionado, incorporando nuevas tecnologías 
                  y mejorando continuamente nuestros procesos para ofrecer soluciones cada vez más 
                  eficientes y de mejor calidad.
                </p>
                <p>
                  Hoy, somos reconocidos por nuestra confiabilidad, calidad y compromiso con la 
                  Satisfacción del cliente, siendo la opción preferida de empresas que buscan 
                  bolsas de plástico personalizadas de primera calidad.
                </p>
              </div>
            </div>
            <div className="rounded-lg h-96 overflow-hidden">
              <img 
                src="/images/empresa.jpg" 
                alt="plásticos Polcen - Nuestra empresa" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Misión y visión */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Target className="h-10 w-10 text-primary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Misión</h3>
              <p className="text-gray-600 text-lg">
                Fabricar bolsas de plástico LDPE de la más alta calidad, personalizadas según 
                las necesidades específicas de nuestros clientes, brindando un servicio 
                excepcional y soluciones innovadoras que superen sus expectativas.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Star className="h-10 w-10 text-primary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestra visión</h3>
              <p className="text-gray-600 text-lg">
                Ser reconocidos como la empresa líder en la fabricación de bolsas de plástico 
                personalizadas en la región, destacándonos por nuestra calidad, Innovación 
                y compromiso con la Satisfacción total del cliente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestros Valores
            </h2>
            <p className="text-lg text-gray-600">
              Los principios que guían nuestro trabajo y definen nuestra cultura empresarial
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-lg shadow-md">
                <div className="flex justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logros */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Nuestros Logros
            </h2>
            <p className="text-xl text-primary-100">
              Cifras que respaldan nuestra experiencia y compromiso
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-200 mb-2">
                  {achievement.number}
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  {achievement.label}
                </h3>
                <p className="text-primary-100">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipo */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestro Equipo
            </h2>
            <p className="text-lg text-gray-600">
              Profesionales comprometidos con la excelencia y la Satisfacción del cliente
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="bg-gray-200 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                  <Users className="h-12 w-12 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">
                  {member.role}
                </p>
                <p className="text-gray-600">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ubicación */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestra Ubicación
            </h2>
            <p className="text-lg text-gray-600">
              Encontranos en Rivadavia
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Información de Contacto
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-primary-600 mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Dirección</h4>
                      <p className="text-gray-600">
                        {companyConfig.address.split(', ').map((part, index) => (
                          <React.Fragment key={index}>
                            {part}
                            {index < companyConfig.address.split(', ').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Phone className="h-6 w-6 text-primary-600 mr-4 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Teléfono</h4>
                      <a href={`tel:${companyConfig.phone}`} className="text-gray-600 hover:text-primary-600">
                        {companyConfig.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Mail className="h-6 w-6 text-primary-600 mr-4 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Email</h4>
                      <a href={`mailto:${companyConfig.email}`} className="text-gray-600 hover:text-primary-600">
                        {companyConfig.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="h-6 w-6 text-primary-600 mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Horarios de atención</h4>
                      <p className="text-gray-600">
                        {companyConfig.hours.replace(' de ', '<br />')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <Link
                    to="/contacto"
                    className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                  >
                    Contactar Ahora
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>

            <div>
              <div className="rounded-lg h-96 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3404.123456789!2d-68.57175370379987!3d-31.51526048777523!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDMwJzU0LjkiUyA2OMKwMzQnMTguMyJX!5e0!3m2!1ses!2sar!4v1234567890123!5m2!1ses!2sar"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación de plásticos Polcen - Santiago de Liniers 1239, Rivadavia, San Juan"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Quieres conocer más sobre nosotros?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Contáctanos y descubre cómo podemos ayudarte con tu proyecto
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="inline-flex items-center px-8 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Solicitar Información
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/productos"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
            >
              Ver Nuestros Productos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Company;



